<script lang="ts">
  import Breadcrumbs from "./Breadcrumbs.svelte";
  import { onDestroy, onMount } from "svelte";
  import Separator from "./Separator.svelte";
  import Switch from "./Switch.svelte";
  import { targetUrl } from "../lib/api-parkito";
  import type { NavbarEntry } from "../lib/navbar";
  import { fillWidth } from "../lib/styling";
  import { push } from "svelte-spa-router";
  import CustomButton from "./CustomButton.svelte";
  import NotificationItem from "./NotificationItem.svelte";
  import * as Toast from "./Toaster/store";
  import { AccessLevel } from "../../apis/parkito";
  import { session } from "$shared/lib/session-parkito";
  import TopNavigationLogo from "./TopNavigationLogo.svelte";

  export let navbarEntries: NavbarEntry[] = [];
  export let env: "mypages" | "dpo" = "dpo";
  export let masqUrl: string;

  onMount(() => {});

  let dropDownMenuVisible = false;
  let notificationMenuVisible = false;
  $: currentHash = window.location.hash;

  let productionSwitch =
    $targetUrl === "https://parkito.parkando.cloud/api";
  let fillWidthSwitch = $fillWidth;

  addEventListener("hashchange", (_) => {
    currentHash = window.location.hash;
  });

  function handleToggle(production: boolean) {
    productionSwitch = production;
    if (production) {
      targetUrl.setUrl("https://parkito.parkando.cloud/api");
    } else {
      targetUrl.setUrl("http://localhost:3000/api");
    }
  }

  function handleWidthToggle(production: boolean) {
    fillWidthSwitch = production;
    fillWidth.setValue(production);
  }

  const handleClickOutside = (event: MouseEvent) => {
    let node: Element | null = event.target as Element;
    let isOutside = true;

    do {
      if (
        node.id === "dropdown-container" ||
        node.id === "toggle-dropdown-menu" ||
        node.id === "notification-container"
      ) {
        isOutside = false;
        break;
      }
      node = node.parentNode as Element;
    } while (node);

    if (isOutside) {
      dropDownMenuVisible = false;
      notificationMenuVisible = false;
    }
  };

  function logout() {
    if ($session) {
      session.clear();
    }
    push("/");
  }

  const notificationItemClicked = (id: string) => {
    Toast.info("Notification clicked: " + id);
  };

  const markAllNotificationsAsRead = () => {
    Toast.info("Markera alla som lästa (ej implementerad)");
  };

  document.addEventListener("mouseup", handleClickOutside);

  onDestroy(() => {
    document.removeEventListener("mouseup", handleClickOutside);
  });

  const goToMasq = () => {
    push(masqUrl);
  };
  const isMasqing = session.isMasqing;
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  id="navbar"
  class="fixed z-50 text-white w-full h-50 px-8 flex items-center"
  class:bg-orange-50={$isMasqing}
  class:bg-green-72={!$isMasqing}
>
  <TopNavigationLogo />
  {#if $isMasqing}
    <div class="ml-2 p-1 border-solid border-2 border-white rounded-md">
      Maskerar som {session.getFormattedName()}
    </div>
  {/if}

  <!-- Breadcrumb -->
  <div class="max-mobile:hidden">
    <Breadcrumbs />
  </div>

  <!-- Navigation items -->
  <div class="flex ml-auto h-full">
    <ul class="hidden lg:flex">
      {#each navbarEntries as { label, hash }}
        <li class="h-full flex items-center px-4 relative text-white">
          <a href={hash}>{label}</a>
          <div
            class="bg-[#87A5A7] h-5 absolute bottom-0 left-0 right-0"
            class:hidden={!currentHash.startsWith(hash)}
          >
          </div>
        </li>
      {/each}
    </ul>

    {#if env === "dpo"}
      <!-- Notification button -->
      <!--
      DISABLED UNTIL IMPLEMENTED
      <button
        class="ml-[8px] mr-[-16px] hover:cursor-pointer"
        id="toggle-dropdown-menu"
        on:click={() => {
          dropDownMenuVisible = false;
          notificationMenuVisible = !notificationMenuVisible;
        }}
      >
        <img
          class="min-w-[24px]"
          src="/icons/menu-notification.svg"
          alt="notification"
        />
      </button>
      -->
    {/if}

    <!-- Drop down button -->
    <button
      class="ml-[24px] p-[8px] mr-[-8px] hover:cursor-pointer"
      id="toggle-dropdown-menu"
      on:click={() => {
        notificationMenuVisible = false;
        dropDownMenuVisible = !dropDownMenuVisible;
      }}
    >
      <img
        class="min-w-[24px]"
        src="/icons/menu-button.svg"
        alt="drop-down-menu"
      />
    </button>
  </div>

  <!-- Drop down menu -->
  {#if dropDownMenuVisible}
    <div
      id="dropdown-container"
      class="absolute bg-[white] right-[32px] top-[48px] w-[300px] } dropdown-menu-wrapper shadow-card"
    >
      <div class="px-[16px]">
        <div class="pb-[8px] visible lg:hidden">
          <h3 class="text-lg font-normal text-[#92ABAF]">Meny</h3>
          {#each navbarEntries as { label, hash }}
            <a
              class="text-lg font-semibold text-[#58686A] pt-[8px] pb-[8px] block"
              href={hash}
              on:click={() => dropDownMenuVisible = false}
            >{label}</a>
          {/each}
        </div>
        <h3 class="text-lg font-normal text-[#92ABAF]">Inställningar</h3>
        {#if         $session?.user?.accesses
          .flatMap((a) => a.level)
          .includes(AccessLevel.ADMIN)}
          <span
            class="text-lg font-semibold text-[#58686A] block pt-[16px] pb-[8px] flex items-center"
          >
            Produktion <Switch
              isOn={productionSwitch}
              onToggle={handleToggle}
              stylingClass="ml-auto"
            />
          </span>
        {/if}
        {#if env === "dpo"}
          <span
            class="text-lg font-semibold text-[#58686A] block pt-[8px] pb-[8px] flex items-center"
          >
            Fyll sidbredd <Switch
              isOn={fillWidthSwitch}
              onToggle={handleWidthToggle}
              stylingClass="ml-auto"
            />
          </span>
          <a
            class="text-lg font-semibold text-[#58686A] pt-[8px] pb-[8px] block"
            href="#/settings"
          >Inställningar</a>
        {/if}
      </div>
      <Separator customClass="my-[16px]" />
      <div class="px-[16px] pb-[12px]">
        <button
          class="text-lg font-semibold text-[#58686A] block pt-[8px] pb-[16px]"
          on:click={logout}
        >
          Logga ut
        </button>
        <p class="text-center text-[#9FB1B2] text-sm pt-8px">
          © Parkando AB {new Date().getFullYear()}<br />
          {#if env === "dpo"}
            Datapark operatör
            <span on:dblclick={goToMasq}>
              {import.meta.env.VITE_DPO_TAG}
            </span>
          {:else}
            Mina sidor {import.meta.env.VITE_MYPAGES_TAG}
          {/if}
        </p>
      </div>
    </div>
  {/if}

  <!-- Notification menu -->
  {#if notificationMenuVisible}
    <div
      id="notification-container"
      class="
        absolute bg-[white] right-[32px] top-[48px] w-[420px]
        dropdown-menu-wrapper shadow-card
      "
    >
      <div>
        <div class="px-[16px] pb-[16px]">
          <h3 class="text-lg font-normal text-[#92ABAF] inline">
            Notifieringar
          </h3>
          <button
            class="text-[14px] text-bg-grey-80 underline float-right hover:text-[#7C8C8E]"
            on:click={markAllNotificationsAsRead}
          >
            Markera alla som lästa
          </button>
        </div>

        <div class="notification-list overflow-y-auto max-h-[480px] pb-[16px]">
          <NotificationItem
            customClass="py-[8px]"
            onClicked={notificationItemClicked}
          />
          <NotificationItem
            customClass="py-[8px]"
            onClicked={notificationItemClicked}
          />
          <NotificationItem
            customClass="py-[8px]"
            onClicked={notificationItemClicked}
          />

          <div class="px-[16px] pt-[16px]">
            <CustomButton
              type="ghost"
              onClick={() => {
                Toast.info(
                  "Hämtar fler notifieringar... (ej implementerad)",
                );
              }}
              customClass="!w-full !max-w-full"
            >Hämta fler notifieringar</CustomButton>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .dropdown-menu-wrapper {
    border-radius: 5px;
    background: #f0f8f9;
    box-shadow: 0px 2px 7px 0px rgba();
    padding: 24px 0px 0px 0px;
  }
  .notification-list {
    scrollbar-width: thin;
  }
</style>
